import React, { useState } from 'react';
import { Star, ShoppingCart, Shield, CheckCircle, Truck, Award, Heart, Eye, Filter, Search, Grid3x3, List } from 'lucide-react';
import product1 from '../images/product1.jpeg'
import product2 from '../images/product2.jpeg'
import product3 from '../images/product3.jpeg'
import product4 from '../images/product4.jpeg'
import product5 from '../images/product5.jpeg'
import product6 from '../images/product6.jpeg'

const ShopNow = () => {
  const [viewMode, setViewMode] = useState('grid');
  const [cartItems, setCartItems] = useState({});
  const [wishlist, setWishlist] = useState({});

  const products = [
    {
      id: 1,
      name: "EMF Protection Phone Case",
      price: "$49.99",
      originalPrice: "$69.99",
      rating: 4.8,
      reviews: 234,
      image: product1,
      description: "Advanced EMF blocking technology for smartphones with 99.9% radiation reduction",
      badge: "Best Seller",
      discount: "30% OFF"
    },
    {
      id: 2,
      name: "EMF Blocking Laptop Pad",
      price: "$79.99",
      originalPrice: "$99.99",
      rating: 4.9,
      reviews: 156,
      image: product2,
      description: "Protect yourself from laptop radiation while maintaining optimal performance",
      badge: "Editor's Choice",
      discount: "20% OFF"
    },
    {
      id: 3,
      name: "EMF Protection Pendant",
      price: "$129.99",
      originalPrice: "$159.99",
      rating: 4.7,
      reviews: 89,
      image: product3,
      description: "Wearable EMF protection for all-day coverage with elegant design",
      badge: "New",
      discount: "19% OFF"
    },
    {
      id: 4,
      name: "EMF Home Shield",
      price: "$199.99",
      originalPrice: "$249.99",
      rating: 4.9,
      reviews: 312,
      image: product4,
      description: "Whole home EMF protection solution covering up to 2000 sq ft",
      badge: "Premium",
      discount: "20% OFF"
    },
    {
      id: 5,
      name: "EMF Bed Canopy",
      price: "$299.99",
      originalPrice: "$399.99",
      rating: 4.8,
      reviews: 78,
      image: product5,
      description: "Create an EMF-free sleeping environment for better rest and recovery",
      badge: "Premium",
      discount: "25% OFF"
    },
    {
      id: 6,
      name: "EMF Meter Pro",
      price: "$89.99",
      originalPrice: "$119.99",
      rating: 4.6,
      reviews: 145,
      image: product6,
      description: "Professional-grade EMF detection and measurement device",
      badge: "Professional",
      discount: "25% OFF"
    }
  ];

  const addToCart = (productId) => {
    setCartItems(prev => ({
      ...prev,
      [productId]: (prev[productId] || 0) + 1
    }));
  };

  const toggleWishlist = (productId) => {
    setWishlist(prev => ({
      ...prev,
      [productId]: !prev[productId]
    }));
  };

  const ProductCard = ({ product }) => (
    <div className="group bg-white rounded-3xl shadow-lg hover:shadow-2xl transition-all duration-500 overflow-hidden hover:-translate-y-2">
      {/* Product Image */}
      <div className="relative h-64 bg-gray-100 overflow-hidden">
        {/* Badge */}
        {product.badge && (
          <div className={`absolute top-4 left-4 px-3 py-1 rounded-full text-xs font-semibold z-10 ${
            product.badge === 'Best Seller' ? 'bg-red-500 text-white' :
            product.badge === 'Editor\'s Choice' ? 'bg-green-500 text-white' :
            product.badge === 'New' ? 'bg-blue-500 text-white' :
            'bg-purple-500 text-white'
          }`}>
            {product.badge}
          </div>
        )}
        
        {/* Discount */}
        {product.discount && (
          <div className="absolute top-4 right-4 bg-orange-500 text-white px-3 py-1 rounded-full text-xs font-semibold">
            {product.discount}
          </div>
        )}

        {/* Product Image */}
        <img 
          src={product.image} 
          alt={product.name}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
        />

        {/* Action Buttons */}
        <div className="absolute bottom-4 right-4 flex space-x-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
          <button
            onClick={() => toggleWishlist(product.id)}
            className={`w-10 h-10 rounded-full flex items-center justify-center transition-colors ${
              wishlist[product.id] ? 'bg-red-500 text-white' : 'bg-white text-gray-600 hover:bg-red-50 hover:text-red-500'
            }`}
          >
            <Heart className={`w-5 h-5 ${wishlist[product.id] ? 'fill-current' : ''}`} />
          </button>
          <button className="w-10 h-10 bg-white text-gray-600 rounded-full flex items-center justify-center hover:bg-blue-50 hover:text-blue-500 transition-colors">
            <Eye className="w-5 h-5" />
          </button>
        </div>
      </div>
      
      {/* Product Info */}
      <div className="p-6">
        <h3 className="text-xl font-bold text-gray-800 mb-2 group-hover:text-blue-600 transition-colors">
          {product.name}
        </h3>
        <p className="text-gray-600 text-sm mb-4">
          {product.description}
        </p>
        
        {/* Rating */}
        <div className="flex items-center mb-4">
          <div className="flex text-yellow-400">
            {[...Array(5)].map((_, i) => (
              <Star key={i} className={`w-4 h-4 ${i < Math.floor(product.rating) ? 'fill-current' : ''}`} />
            ))}
          </div>
          <span className="text-gray-600 text-sm ml-2">
            {product.rating} ({product.reviews} reviews)
          </span>
        </div>
        
        {/* Price and Cart */}
        <div className="flex items-center justify-between">
          <div className="flex flex-col">
            <div className="flex items-center space-x-2">
              <span className="text-2xl font-bold text-blue-600">
                {product.price}
              </span>
              {product.originalPrice && (
                <span className="text-sm text-gray-400 line-through">
                  {product.originalPrice}
                </span>
              )}
            </div>
          </div>
          <button 
            onClick={() => addToCart(product.id)}
            className="group bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white px-6 py-3 rounded-xl font-medium transition-all duration-300 flex items-center space-x-2 hover:scale-105 shadow-lg"
          >
            <ShoppingCart className="w-4 h-4" />
            <span>{cartItems[product.id] ? `Add More (${cartItems[product.id]})` : 'Add to Cart'}</span>
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      {/* Header */}
      <div className="bg-white/80 backdrop-blur-md shadow-lg">
        <div className="max-w-7xl mx-auto px-6 py-8">
          <div className="text-center mb-8">
            <div className="inline-flex items-center bg-blue-100 text-blue-800 px-4 py-2 rounded-full text-sm font-medium mb-4">
              <Shield className="w-4 h-4 mr-2" />
              Premium EMF Protection
            </div>
            <h1 className="text-5xl font-bold bg-gradient-to-r from-gray-800 via-blue-600 to-purple-600 bg-clip-text text-transparent mb-4">
              EMF Protection Store
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Discover our range of scientifically-tested EMF protection products designed 
              to safeguard you and your family from harmful electromagnetic radiation.
            </p>
          </div>

          {/* Search and Filters */}
          <div className="flex flex-col md:flex-row items-center justify-between space-y-4 md:space-y-0">
            <div className="flex items-center space-x-4">
              <div className="relative">
                <Search className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
                <input
                  type="text"
                  placeholder="Search products..."
                  className="pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <button className="flex items-center space-x-2 px-4 py-2 border border-gray-200 rounded-lg hover:bg-gray-50">
                <Filter className="w-4 h-4" />
                <span>Filters</span>
              </button>
            </div>
            
            <div className="flex items-center space-x-2">
              <span className="text-sm text-gray-600">View:</span>
              <button
                onClick={() => setViewMode('grid')}
                className={`p-2 rounded ${viewMode === 'grid' ? 'bg-blue-500 text-white' : 'text-gray-600 hover:bg-gray-100'}`}
              >
                <Grid3x3 className="w-4 h-4" />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`p-2 rounded ${viewMode === 'list' ? 'bg-blue-500 text-white' : 'text-gray-600 hover:bg-gray-100'}`}
              >
                <List className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-12">
        {/* Product Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
          {products.map((product) => (
            <ProductCard key={product.id} product={product} />
          ))}
        </div>

        {/* Features Section */}
        <div className="bg-white/80 backdrop-blur-sm rounded-3xl p-10 shadow-xl border border-white/20 mb-12">
          <h2 className="text-4xl font-bold text-center bg-gradient-to-r from-gray-800 via-blue-600 to-purple-600 bg-clip-text text-transparent mb-12">
            Why Choose EMF Shop Stream?
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center group">
              <div className="w-20 h-20 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-3xl mx-auto mb-6 flex items-center justify-center group-hover:scale-110 transition-transform duration-300 shadow-lg">
                <Award className="w-10 h-10 text-white" />
              </div>
              <h3 className="text-2xl font-bold mb-4 text-gray-800">Scientifically Tested</h3>
              <p className="text-gray-600 leading-relaxed">All products undergo rigorous testing in certified laboratories for maximum EMF protection and safety standards</p>
            </div>
            <div className="text-center group">
              <div className="w-20 h-20 bg-gradient-to-r from-green-500 to-emerald-500 rounded-3xl mx-auto mb-6 flex items-center justify-center group-hover:scale-110 transition-transform duration-300 shadow-lg">
                <CheckCircle className="w-10 h-10 text-white" />
              </div>
              <h3 className="text-2xl font-bold mb-4 text-gray-800">30-Day Guarantee</h3>
              <p className="text-gray-600 leading-relaxed">Not satisfied with your purchase? Return within 30 days for a full refund, no questions asked</p>
            </div>
            <div className="text-center group">
              <div className="w-20 h-20 bg-gradient-to-r from-purple-500 to-pink-500 rounded-3xl mx-auto mb-6 flex items-center justify-center group-hover:scale-110 transition-transform duration-300 shadow-lg">
                <Truck className="w-10 h-10 text-white" />
              </div>
              <h3 className="text-2xl font-bold mb-4 text-gray-800">Free Fast Shipping</h3>
              <p className="text-gray-600 leading-relaxed">Free express shipping on all orders over $75. Get your protection products delivered in 2-3 business days</p>
            </div>
          </div>
        </div>

        {/* Trust Indicators */}
        <div className="bg-gradient-to-r from-gray-800 to-gray-900 rounded-3xl p-10 text-white text-center">
          <h3 className="text-2xl font-bold mb-6">Trusted by Over 50,000 Customers</h3>
          <div className="grid md:grid-cols-4 gap-8">
            <div>
              <div className="text-3xl font-bold text-blue-400 mb-2">99.9%</div>
              <div className="text-gray-300">EMF Reduction</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-green-400 mb-2">50K+</div>
              <div className="text-gray-300">Happy Customers</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-purple-400 mb-2">4.8/5</div>
              <div className="text-gray-300">Average Rating</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-orange-400 mb-2">24/7</div>
              <div className="text-gray-300">Customer Support</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ShopNow;